/**
 * Share Lifecycle Manager - Manages shared doc CRDT documents
 *
 * This module handles the lifecycle of shared doc sync connections,
 * including initialization, cleanup, and state tracking.
 *
 * Uses a callback injection pattern to avoid circular dependencies with crdt-bridge.js.
 * Call initShareLifecycle() with callbacks before using other functions.
 */

import { createLogger } from "../core/logger.js";
import { emit, Events } from "../core/events.js";
import { YjsDocManager } from "./yjs-doc.js";
import { SyncManager } from "./websocket.js";
import { VaultStorage } from "../crypto/vault-storage.js";
import { validateTree } from "../core/validation.js";
import {
  getActiveShareId,
  setActiveShareId,
  setShareRole,
  removeShareRole,
  getShareRole,
} from "./share-state.js";
import {
  mergeLocalDimensionsIntoTree,
  getLocalDimensionsSize,
  clearLocalDimensionsRecursive,
} from "./dimension-manager.js";
import * as undoManager from "../core/undo-manager.js";
import { ensureNotesSerializer } from "./notes-serializer-facade.js";

const log = createLogger("Share Lifecycle");
const logError = (...args) => log.error(...args);
const logWarn = (...args) => log.warn(...args);
const logInfo = (...args) => log.info(...args);
const logDebug = (...args) => log.debug(...args);

// ============================================
// State Maps for Shared Docs
// ============================================

/** @type {Map<string, YjsDocManager>} */
const sharedDocManagers = new Map(); // shareId -> YjsDocManager

/** @type {Map<string, SyncManager>} */
const sharedSyncManagers = new Map(); // shareId -> SyncManager

/** @type {Map<string, Function>} */
const sharedObserverCleanups = new Map(); // shareId -> cleanup function

/** @type {Map<string, number>} */
const sharedUpdateDebounceTimers = new Map(); // shareId -> timer ID

const SHARED_UPDATE_DEBOUNCE_MS = 50;

// ============================================
// Callback Injection
// ============================================

/**
 * Callbacks injected from crdt-bridge.js to handle operations
 * that require access to crdt-bridge internals
 */
let callbacks = {
  /** Setup CRDT observer for a doc manager */
  setupCRDTObserver: null,
  /** Check if document is currently loading */
  isDocumentLoading: null,
  /** Get current syncing depth */
  getSyncingDepth: null,
  /** Increment syncing depth */
  incrementSyncingDepth: null,
  /** Decrement syncing depth */
  decrementSyncingDepth: null,
  /** Set isDocumentLoading callback */
  setIsDocumentLoadingCallback: null,
  /** Get store reference */
  getStore: null,
  /** Handle share revoked (for error callback) */
  handleShareRevoked: null,
  /** Get the main document ID (for routing undo back to main on share exit) */
  getMainDocId: null,
};

/**
 * Initialize the share lifecycle manager with callbacks
 * Must be called before using other functions
 * @param {Object} cbs - Callback functions
 */
export function initShareLifecycle(cbs) {
  callbacks = { ...callbacks, ...cbs };
  logDebug("Share lifecycle initialized with callbacks");
}

// ============================================
// Public API - Lifecycle Management
// ============================================

const sharedDocStops = new Map();

/**
 * Initialize sync for a shared doc
 * @param {string} shareId - The share UUID (used as doc ID)
 * @param {CryptoKey} encryptionKey - DEK for this share
 * @param {Object} options - Configuration options
 * @returns {Promise<void>}
 */
export async function initSharedDoc(shareId, encryptionKey, options = {}) {
  await sharedDocStops.get(shareId);
  if (sharedDocManagers.has(shareId)) {
    logDebug("Shared doc already initialized:", shareId);
    return;
  }

  logInfo("Initializing shared doc:", shareId);

  // Store the isDocumentLoading callback for use in update handlers
  if (options.isDocumentLoading && callbacks.setIsDocumentLoadingCallback) {
    callbacks.setIsDocumentLoadingCallback(options.isDocumentLoading);
  }

  // Track whether we set activeShareId for cleanup on error
  let didSetActiveShare = false;

  try {
    // Create Yjs document manager for this share
    const sharedDocManager = new YjsDocManager(shareId, {
      userId: options.userId,
      onUpdate: () => handleSharedDocUpdate(shareId),
    });

    // Initialize persistence
    await sharedDocManager.initPersistence();

    // Same guarantee as CRDTBridge.init: yxml-v1 docs serialize note
    // fragments synchronously inside toTree(), so the code-split serializer
    // chunk must be resident before this share's first rebuild.
    // Non-fatal on failure — toTree() falls back to cached/flat note strings.
    if (sharedDocManager.metadata.get("notesFormat") === "yxml-v1") {
      try {
        await ensureNotesSerializer();
      } catch (err) {
        console.warn("[ShareLifecycle] Notes serializer unavailable — note bodies may be stale:", err);
      }
    }

    // Create sync manager
    const sharedSyncManager = new SyncManager({
      isReadOnly: () => getShareRole(shareId) === "viewer",
      onStateChange: (newState, oldState) =>
        handleSharedConnectionStateChange(shareId, newState, oldState),
      onPresenceChange: options.onPresenceChange,
      onError: (error) => handleSharedSyncError(shareId, error),
      onRemoteUpdate: () => handleSharedRemoteUpdate(shareId),
      onUnshared: (info) => {
        if (callbacks.handleShareRevoked) {
          callbacks.handleShareRevoked(shareId, info, options);
        }
      },
      onAutoUnshared: (info) => handleAutoUnshared(shareId, info),
    });

    // Store managers and role
    sharedDocManagers.set(shareId, sharedDocManager);
    sharedSyncManagers.set(shareId, sharedSyncManager);
    if (options.role) {
      setShareRole(shareId, options.role);
    }

    // Set this as the active share (route Store events here)
    // Skip if suppressActiveShare is set (used for background initialization like attach)
    if (!options.suppressActiveShare) {
      setActiveShareId(shareId);
      didSetActiveShare = true;
    }

    // Set encryption key and connect
    sharedSyncManager.setEncryptionKey(encryptionKey);
    await sharedSyncManager.connect(shareId, sharedDocManager);

    // Register observer AFTER successful connection
    // This prevents orphaned observers if connection fails
    if (callbacks.setupCRDTObserver) {
      const observerCleanup = callbacks.setupCRDTObserver(sharedDocManager, shareId);
      sharedObserverCleanups.set(shareId, observerCleanup);
    }

    // Bind tree UndoManager for this shared doc. If we're routing Store
    // events here (i.e., this is now the active view), also mark it active
    // so Ctrl+Z operates on this doc's history.
    undoManager.bind(sharedDocManager);
    if (!options.suppressActiveShare) {
      undoManager.setActive(shareId);
    }

    logInfo(
      "Shared doc initialized:",
      shareId,
      options.suppressActiveShare ? "(background mode)" : "(set as active)",
    );
  } catch (error) {
    // Clean up on error to prevent stale activeShareId
    logError("Failed to initialize shared doc:", shareId, error);
    if (didSetActiveShare) {
      setActiveShareId(null);
    }
    // Clean up any partially created managers
    await stopSharedDocSync(shareId);
    throw error;
  }
}

/**
 * Stop sync for a shared doc
 * Also clears local dimensions and CRDT observer
 * @param {string} shareId
 */
export function stopSharedDocSync(shareId) {
  if (sharedDocStops.has(shareId)) return sharedDocStops.get(shareId);
  const stopping = stopSharedDocSyncImpl(shareId);
  sharedDocStops.set(shareId, stopping);
  void stopping.finally(() => {
    if (sharedDocStops.get(shareId) === stopping) sharedDocStops.delete(shareId);
  }).catch(() => {});
  return stopping;
}

async function stopSharedDocSyncImpl(shareId) {
  // Clear debounce timer to prevent memory leak
  const existingTimer = sharedUpdateDebounceTimers.get(shareId);
  if (existingTimer) {
    clearTimeout(existingTimer);
    sharedUpdateDebounceTimers.delete(shareId);
  }

  // Clean up CRDT observer for this shared doc
  const observerCleanup = sharedObserverCleanups.get(shareId);
  if (observerCleanup) {
    observerCleanup();
    sharedObserverCleanups.delete(shareId);
  }

  const syncManager = sharedSyncManagers.get(shareId);
  if (syncManager) {
    await syncManager.disconnect();
    sharedSyncManagers.delete(shareId);
  }

  const sharedDocMgr = sharedDocManagers.get(shareId);
  if (sharedDocMgr) {
    // Clear local dimensions for nodes in this shared doc
    clearLocalDimensionsForShare(shareId, sharedDocMgr);
    // Unbind UndoManager BEFORE destroying the Y.Doc.
    undoManager.unbind(shareId);
    await sharedDocMgr.destroy();
    sharedDocManagers.delete(shareId);
  }

  // Clean up role tracking
  removeShareRole(shareId);

  if (getActiveShareId() === shareId) {
    setActiveShareId(null);
  }

  logInfo("Stopped shared doc sync:", shareId);
}

/**
 * Stop sync for all shared docs
 */
export async function stopAllSharedDocSync() {
  const shareIds = Array.from(sharedDocManagers.keys());
  logInfo("Stopping", shareIds.length, "shared doc syncs:", shareIds);
  const stopPromises = [];
  for (const shareId of shareIds) {
    stopPromises.push(stopSharedDocSync(shareId));
  }
  await Promise.all(stopPromises);
  logInfo("Stopped all shared doc syncs, remaining:", sharedDocManagers.size);
}

// ============================================
// Public API - Accessors
// ============================================

/**
 * Get the Yjs doc manager for a shared doc
 * @param {string} shareId
 * @returns {YjsDocManager|null}
 */
export function getSharedDocManager(shareId) {
  return sharedDocManagers.get(shareId) || null;
}

/**
 * Get the sync manager for a shared doc
 * @param {string} shareId
 * @returns {SyncManager|null}
 */
export function getSharedSyncManager(shareId) {
  return sharedSyncManagers.get(shareId) || null;
}

/**
 * Wait for a shared doc's sync connection to be ready
 * @param {string} shareId
 * @param {number} timeout - Max time to wait in ms (default 10000)
 * @returns {Promise<boolean>} Resolves true if synced, false if timeout/error
 */
export async function waitForSharedSync(shareId, timeout = 10000) {
  const syncManager = sharedSyncManagers.get(shareId);
  if (!syncManager) {
    logWarn("No sync manager for share:", shareId);
    return false;
  }
  return syncManager.waitForSync(timeout);
}

/**
 * Set the currently active shared doc
 * @param {string|null} shareId
 */
export function setActiveShare(shareId) {
  setActiveShareId(shareId);
  // Route undo/redo to this doc (or back to main when shareId is null).
  // When returning to the main doc we look up its docId via the callback.
  if (shareId) {
    undoManager.setActive(shareId);
  } else if (callbacks.getMainDocId) {
    undoManager.setActive(callbacks.getMainDocId());
  } else {
    undoManager.setActive(null);
  }
  logInfo("Active share set to:", shareId);
}

/**
 * Get the currently active shared doc ID
 * @returns {string|null}
 */
export function getActiveShare() {
  return getActiveShareId();
}

/**
 * Check if a share is initialized
 * @param {string} shareId
 * @returns {boolean}
 */
export function hasSharedDoc(shareId) {
  return sharedDocManagers.has(shareId);
}

/**
 * Get all active shared doc IDs
 * @returns {string[]}
 */
export function getActiveSharedDocs() {
  return Array.from(sharedDocManagers.keys());
}

/**
 * Get the current size of the sharedDocManagers map (for logging)
 * @returns {number}
 */
export function getSharedDocManagersSize() {
  return sharedDocManagers.size;
}

/**
 * Clear all debounce timers (used during destroy)
 */
export function clearAllDebounceTimers() {
  for (const timer of sharedUpdateDebounceTimers.values()) {
    clearTimeout(timer);
  }
  sharedUpdateDebounceTimers.clear();
}

// ============================================
// Internal Handlers
// ============================================

/**
 * Handle update from a shared doc's Yjs doc (debounced)
 * @param {string} shareId
 */
function handleSharedDocUpdate(shareId) {
  logInfo("handleSharedDocUpdate called for shareId:", shareId);
  // Clear existing timer for this shareId
  const existingTimer = sharedUpdateDebounceTimers.get(shareId);
  if (existingTimer) {
    clearTimeout(existingTimer);
  }

  // Schedule the actual update
  const timer = setTimeout(() => {
    logInfo("handleSharedDocUpdate timer fired for shareId:", shareId);
    sharedUpdateDebounceTimers.delete(shareId);
    _handleSharedDocUpdateCore(shareId);
  }, SHARED_UPDATE_DEBOUNCE_MS);

  sharedUpdateDebounceTimers.set(shareId, timer);
}

/**
 * Core handler for shared doc updates (called after debounce)
 * Exported for use by crdt-bridge.js for synchronous local updates.
 * @param {string} shareId
 */
export function _handleSharedDocUpdateCore(shareId) {
  // Skip if share-lifecycle callbacks aren't initialized yet
  // This happens when a shared map is opened as the first action (before any personal map)
  // because CRDTBridge.init() hasn't been called yet to set up initShareLifecycle()
  if (!callbacks.getStore) {
    logDebug("Share lifecycle not initialized yet, skipping update for:", shareId);
    return;
  }

  // Check syncing depth via callback
  if (callbacks.getSyncingDepth && callbacks.getSyncingDepth() > 0) return;

  // Skip during document transitions to prevent overwriting Store with stale data
  if (callbacks.isDocumentLoading && callbacks.isDocumentLoading()) {
    logDebug("Document loading, skipping shared doc update:", shareId);
    return;
  }

  const sharedDocManager = sharedDocManagers.get(shareId);
  if (!sharedDocManager) return;

  logDebug("Shared doc update:", shareId);

  if (callbacks.incrementSyncingDepth) {
    callbacks.incrementSyncingDepth();
  }

  try {
    // Convert Yjs doc back to tree
    const tree = sharedDocManager.toTree();
    if (!tree) return;

    // Validate tree
    if (!validateTree(tree, (msg) => logError(`[SharedDoc:${shareId}] Validation:`, msg))) {
      logWarn('[ShareLifecycle] Shared doc validation failed for', shareId);
      return;
    }

    // Log warning if root ID format is unexpected
    // Valid formats: UUIDs (with hyphens), 'root', or legacy 'n'-prefixed IDs (e.g., n1234567890abcdef)
    if (tree.id && !tree.id.includes('-') && tree.id !== 'root' && !tree.id.startsWith('n')) {
      logWarn(`[SharedDoc:${shareId}] Unexpected root ID format:`, tree.id);
    }

    // Validate tree root ID matches CRDT metadata
    const metadata = sharedDocManager.doc.getMap("metadata");
    const expectedRootId = metadata.get("rootId");
    if (expectedRootId && tree.id && tree.id !== expectedRootId) {
      logError(`[SharedDoc:${shareId}] Root ID mismatch. Expected: ${expectedRootId}, Got: ${tree.id}`);
      return;
    }

    // Check if tree has null children (indicates incomplete sync)
    const hasNullChildren = (node) => {
      if (!node || !node.children) return false;
      for (const child of node.children) {
        if (!child) return true;
        if (hasNullChildren(child)) return true;
      }
      return false;
    };

    if (hasNullChildren(tree)) {
      logWarn("Tree has null children, skipping render - waiting for complete sync");
      return;
    }

    const activeShareId = getActiveShareId();
    logDebug("handleSharedDocUpdate - activeShareId:", activeShareId, "shareId:", shareId, "match:", activeShareId === shareId);

    const store = callbacks.getStore ? callbacks.getStore() : null;
    if (!store) {
      logWarn("No store available for shared doc update");
      return;
    }

    if (activeShareId === shareId) {
      // Viewing the shared document directly - replace entire store
      logDebug("Updating shared document view:", shareId);
      try {
        // Merge local dimensions before updating Store
        if (getLocalDimensionsSize() > 0) {
          mergeLocalDimensionsIntoTree(tree);
        }

        store._setDataFromCRDT(tree, {
          source: 'shared-doc-update',
          isInitialLoad: false,  // Critical: prevents history reset
          preserveFocus: true,
          preserveSelection: true,
          docId: shareId,
          isShared: store.get("isShared"),
          shareRole: store.get("shareRole"),
        });
        logDebug("_setDataFromCRDT completed successfully");
      } catch (err) {
        logError("Error in _setDataFromCRDT:", err);
      }
    } else {
      // Not viewing this shared doc - skip update
      logDebug("Share", shareId, "is not the active view - update skipped");
    }

    emit(Events.SYNC_REMOTE_UPDATE, {
      source: "shared-doc",
      shareId,
    });
  } finally {
    if (callbacks.decrementSyncingDepth) {
      callbacks.decrementSyncingDepth();
    }
  }
}

/**
 * Handle connection state change for a shared doc
 */
function handleSharedConnectionStateChange(shareId, newState, oldState) {
  logInfo(`Shared ${shareId} connection: ${oldState} -> ${newState}`);

  emit(Events.SYNC_STATE_CHANGED, {
    shareId,
    state: newState,
    previousState: oldState,
  });
}

/**
 * Handle sync error for a shared doc
 */
function handleSharedSyncError(shareId, error) {
  logError(`Shared ${shareId} error:`, error);

  // If user was removed from the share, trigger revocation cleanup
  if (error && error.type === "removed") {
    logInfo("User removed from share, triggering cleanup");
    if (callbacks.handleShareRevoked) {
      callbacks.handleShareRevoked(shareId, { reason: "removed" }, {});
    }
    return;
  }

  emit(Events.SYNC_ERROR, { shareId, error });
}

/**
 * Handle auto-unshare (0 users remaining) - owner notification to clear chain badge
 */
async function handleAutoUnshared(shareId, info) {
  logInfo("Share auto-stopped (0 users):", shareId, info);

  // In the unified doc model, shareId IS the owner's docId. Deleting the doc
  // would destroy the owner's document data. Instead, just clear the shared
  // state — the document remains in the vault as a personal (unshared) doc.
  // The vault panel's cache of owned shares follows (interfaces/classic/ui/vault/state.js).
  emit(Events.OWNED_SHARES_CHANGED, { owned: [] });

  logDebug("Auto-unshare complete for doc:", shareId, "— doc preserved in vault");
}

/**
 * Handle remote update for a shared doc
 */
function handleSharedRemoteUpdate(shareId) {
  logDebug("Remote update for shared doc:", shareId);
  emit(Events.SYNC_REMOTE_UPDATE, {
    source: "websocket",
    shareId,
  });
}

/**
 * Clear local dimension cache for all nodes in a shared doc
 * @param {string} shareId - The share ID
 * @param {YjsDocManager} sharedDocMgr - The doc manager for this share
 */
function clearLocalDimensionsForShare(shareId, sharedDocMgr) {
  if (!sharedDocMgr) return;

  try {
    // Get all node IDs from the shared doc
    const tree = sharedDocMgr.toTree();
    if (tree) {
      clearLocalDimensionsRecursive(tree);
    }
  } catch (err) {
    logWarn("Error clearing local dimensions for share:", shareId, err);
  }
}


/** Used before explicit logout clears local caches. */
export function hasPendingWrites() {
  return [...sharedSyncManagers.values()].some(manager => manager.hasPendingWrites());
}

/** Sign-out's last sync of shared maps: send, then wait for the server's confirmations. */
export async function settlePendingWrites(timeoutMs = 10000) {
  const managers = [...sharedSyncManagers.values()];
  await Promise.all(managers.map((manager) => manager._flushPendingDeltas?.().catch(() => {})));
  const settled = await Promise.all(managers.map((manager) => manager.flushPendingAcks?.(timeoutMs) ?? true));
  return settled.every(Boolean);
}
